import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { ReportTypes } from 'dashboard/enums/report-type.enum';
import { ScheduledReportFrequency } from 'dashboard/enums/scheduled-report-frequency.enum';
import { ScheduledReportRunStatusType } from 'dashboard/enums/scheduled-report-run-status-type.enum';
import { ScheduledReportType } from 'dashboard/enums/scheduled-report-type.enum';
import { DashboardListingsStatusSummary } from 'dashboard/interfaces/dashboard-listings-status-summary.interface';
import { DashboardListingsNotUpdated } from 'dashboard/interfaces/dashboard-not-updated-listings.interface';
import { ScheduledReportSftpSetting } from 'dashboard/interfaces/scheduled-report-sftp-setting.interface';
import { ScheduledReport } from 'dashboard/interfaces/scheduled-report.interface';
import { Observable, map } from 'rxjs';
import { dayArray } from 'shared/constants/days.constants';
import { BYPASS_ERROR_HANDLER_FOR_ERROR_CODES } from 'shared/interceptors/error.interceptor';
import { Days, IndividualDay } from 'shared/interfaces/days-of-week.interface';
import { BatchOperationTypes } from '../enums/batch-operation-types.enum';

const LISTING_API_URL = environment.BASE_URL + 'listingApi/';

const urls = {
    dashboardSummary: 'settings/dashboard-summary',
    notUpdatedListings: 'settings/dashboard-inactive-listings',
    downloadReport: 'settings/reports',
    availableDownloadReportTypes: 'settings/available-report-types',
    batchOperations: 'values/batch-create-from-file',
};

@Injectable()
export class DashboardService {
    private apollo: ApolloBase;

    constructor(
        private http: HttpClient,
        private apolloProvider: Apollo,
        private translateService: TranslateService,
    ) {
        this.apollo = this.apolloProvider.use('listingAPI');
    }

    public getPublicationStatusWidgetData(): Observable<DashboardListingsStatusSummary> {
        return this.http.get(LISTING_API_URL + urls.dashboardSummary) as Observable<DashboardListingsStatusSummary>;
    }

    public getNotUpdatedListings(): Observable<DashboardListingsNotUpdated[]> {
        return this.http.get(LISTING_API_URL + urls.notUpdatedListings) as Observable<DashboardListingsNotUpdated[]>;
    }

    public getAvailableDownloadReportTypes(): Observable<ReportTypes[]> {
        return this.http.get(LISTING_API_URL + urls.availableDownloadReportTypes) as Observable<ReportTypes[]>;
    }

    public downloadReport(languageISOCode: string, reportType: ReportTypes): Observable<any> {
        return this.http.get(LISTING_API_URL + urls.downloadReport, {
            context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [400, 404, 502]),
            params: { reportType: reportType, languageISOCode: languageISOCode },
        }) as Observable<any>;
    }

    public batchOperationUpload(file: File, batchOperationType?: BatchOperationTypes): Observable<any> {
        const formData = new FormData();
        formData.append('file', file);

        return this.http.post(LISTING_API_URL + urls.batchOperations, formData, { responseType: 'text' });
    }

    public getScheduledReports(): Observable<ScheduledReport[]> {
        return this.apollo
            .query({
                query: gql`
                    query {
                        scheduledReports {
                            searchScheduledReports(searchQuery: { pageNumber: 1, pageSize: 2000, orderDirection: ASC }) {
                                data {
                                    dayOfMonth
                                    id
                                    lastRunTimeOutput
                                    nextRunTimeOutput
                                    scheduledReportErrorLinks {
                                        id
                                        scheduledReportErrorId
                                        scheduledReportError {
                                            errorKey
                                            id
                                            message
                                        }
                                    }
                                    scheduledReportFrequencyValue
                                    scheduledReportStatusValue
                                    scheduledReportTypeValue
                                }
                            }
                        }
                    }
                `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.scheduledReports.searchScheduledReports.data;
                }),
                map((data) => {
                    return data.map((item: any) => {
                        return {
                            id: item.id,
                            reportName: item.scheduledReportTypeValue,
                            scheduleType: item.scheduledReportFrequencyValue as ScheduledReportFrequency,
                            lastRunTime: item.lastRunTimeOutput,
                            lastRunStatus: {
                                status: item.scheduledReportStatusValue as ScheduledReportRunStatusType,
                                errors: item.scheduledReportErrorLinks.map((error: any) => error.scheduledReportError.message),
                                warnings: [],
                            },
                            nextRunTime: item.nextRunTimeOutput,
                        };
                    });
                }),
            );
    }

    public getScheduledReportById(id: number): Observable<{
        scheduledReportType: any;
        frequency: any;
        days: Days;
        dayOfMonth: any;
        time: any;
        emails: string[];
    }> {
        return this.apollo
            .query({
                query: gql`
                query getScheduledReports {
                    scheduledReports {
                        searchScheduledReports(
                            searchQuery: {
                                pageNumber: 1,
                                pageSize: 2000,
                                orderDirection: ASC
                            }
                            filtering: {
                                searchTerm: "${id}"
                            }
                        ) {
                            data {
                                dayOfMonth
                                daysToTriggerValue
                                emailBody
                                emailSubject
                                id
                                scheduledReportEmailRecipientLinks {
                                    scheduledReportEmailRecipient {
                                        userEmail
                                    }
                                }
                                runTimeOutput
                                scheduledReportFrequencyValue
                                scheduledReportTypeValue
                                sftpSettingLinks {
                                    sftpSetting {
                                        id
                                    }
                                }
                            }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.scheduledReports.searchScheduledReports.data[0];
                }),
                map((data) => {
                    return {
                        id: data.id,
                        scheduledReportType: data.scheduledReportTypeValue,
                        frequency: data.scheduledReportFrequencyValue as ScheduledReportFrequency,
                        days: this.mapDayBitmaskToObject(data.daysToTriggerValue),
                        dayOfMonth: data.dayOfMonth,
                        time: data.runTimeOutput,
                        emailSettings: {
                            subject: data.emailSubject,
                            body: data.emailBody,
                        },
                        emails: data.scheduledReportEmailRecipientLinks.map((email: any) => email.scheduledReportEmailRecipient.userEmail),
                        selectedSftpSettingsIds: data.sftpSettingLinks.map((sftp: any) => sftp.sftpSetting.id),
                    };
                }),
            );
    }

    public getSftpSettingsForId(id: string) {
        return this.apollo
            .query({
                query: gql`
                query {
                    sftpSettings {
                        searchSftpSettings(
                            filtering: {
                                searchTerm: "${id}"
                            }
                        ) {
                            data {
                                id
                                userId
                                siteName
                                host
                                port
                                loginTypeValue
                                userName
                                password
                                key
                            }
                            statusCode
                            message
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.sftpSettings.searchSftpSettings.data[0] ?? {};
                }),
                map((data) => {
                    return {
                        sftpSiteName: [data.siteName],
                        host: data.host,
                        port: data.port,
                        loginType: parseInt(data.loginTypeValue),
                        username: data.userName,
                        password: data.password,
                        key: data.key,
                    };
                }),
            );
    }

    public testSftpSettings({ id, data }: { id?: string; data?: any }): Observable<any> {
        if (id) {
            return this.testExistingSftpSettings(id, data);
        } else {
            return this.testNewSftpSettings(data);
        }
    }

    public testExistingSftpSettings(id: string, data: any): Observable<any> {
        return this.apollo
            .query({
                query: gql`
                mutation {
                    sftpSettings {
                        testConnectionOnExistingSftpSetting(
                            sftpSettingId: "${id}"
                            sftpSetting: ${this.deleteEmptyAndPrepareDataForGraphQL({
                                host: data.host,
                                port: data.port,
                                userName: data.username,
                            })}
                        ) {
                            message
                            statusCode
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.sftpSettings.testConnectionOnExistingSftpSetting.data;
                }),
            );
    }

    public testNewSftpSettings(data: any): Observable<any> {
        return this.apollo
            .query({
                query: gql`
                mutation {
                    sftpSettings {
                        testConnectionOnSftpSetting(
                            sftpSetting: ${this.deleteEmptyAndPrepareDataForGraphQL({
                                siteName: data.sftpSiteName[0],
                                host: data.host,
                                port: data.port,
                                loginTypeValue: Number(data.loginType),
                                userName: data.username,
                                password: data.password,
                                key: data.key,
                            })}
                        ) {
                            message
                            statusCode
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.sftpSettings.testConnectionOnSftpSetting.data;
                }),
            );
    }

    public getScheduledReportRenderData(): Observable<{
        scheduledReportTypes: { label: string; value: ScheduledReportType }[];
        emailSettingsDefaults: { subject: string; body: string };
        emailList: string[];
        sftpData: ScheduledReportSftpSetting[];
    }> {
        return this.apollo
            .query({
                query: gql`
                    query getScheduledReportRequiredInformation {
                        scheduledReports {
                            getRequiredScheduledReportInformation {
                                data {
                                    emailSubject
                                    emailBody
                                    emailRecipients
                                    scheduledReportTypeValues
                                    sftpData {
                                        sftpSettingId
                                        siteName
                                    }
                                }
                            }
                        }
                    }
                `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.scheduledReports.getRequiredScheduledReportInformation.data;
                }),
                map((data) => {
                    return {
                        scheduledReportTypes: data.scheduledReportTypeValues.map((item: string) => {
                            // @ts-ignore
                            return { label: item, value: ScheduledReportType[item] };
                        }),
                        emailSettingsDefaults: {
                            subject: data.emailSubject,
                            body: data.emailBody,
                        },
                        emailList: data.emailRecipients,
                        sftpData: data.sftpData.map((item: any) => {
                            return { sftpSettingId: item.sftpSettingId, siteName: item.siteName };
                        }),
                    };
                }),
            );
    }

    public saveSftpData(data: any, id?: string): Observable<any> {
        if (id) {
            return this.updateSftpSettings(data, id);
        } else {
            return this.createSftpSettings(data);
        }
    }

    public createSftpSettings(data: any): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation {
                    sftpSettings {
                        addSftpSetting (
                            sftpSetting: ${this.deleteEmptyAndPrepareDataForGraphQL({
                                siteName: data.sftpSiteName[0],
                                host: data.host,
                                port: data.port,
                                loginTypeValue: Number(data.loginType),
                                userName: data.username,
                                password: data.password,
                                key: data.key,
                            })}
                        ) {
                            message
                            statusCode
                            data {
                                id
                            }
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.sftpSettings.addSftpSetting.data;
                }),
            );
    }

    public updateSftpSettings(data: any, id: string): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation {
                    sftpSettings {
                        updateSftpSetting(
                            sftpSettingId: "${id}",
                            newSftpSetting: ${this.deleteEmptyAndPrepareDataForGraphQL({
                                host: data.host,
                                port: data.port,
                                userName: data.username,
                                siteName: data.sftpSiteName?.[0],
                            })}
                        ) {
                            message
                            statusCode
                            data {
                                id
                            }
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    // Data will be null
                    return response.data;
                }),
            );
    }

    public saveScheduledReport(data: any, id: string): Observable<any> {
        if (id) {
            return this.updateScheduledReport(data, id);
        } else {
            return this.createScheduledReport(data);
        }
    }

    private updateScheduledReport(data: any, id: string): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation {
                    scheduledReports {
                        updateScheduledReport(
                            scheduledReportId: "${id}",
                            newScheduledReport: ${this.deleteEmptyAndPrepareDataForGraphQL({
                                scheduledReportTypeValue: data.scheduledReportType,
                                dayOfMonth: data.dayOfMonth,
                                daysToTriggerValue: data.days ? this.mapDayObjectToBitmask(data.days) : undefined,
                                emailBody: data.emailSettings?.body,
                                emailSubject: data.emailSettings?.subject,
                                runTimeInput: data.time,
                                emailRecipientsInput: data.emails ?? [],
                                scheduledReportFrequencyValue: data.frequency,
                                languageIsoCode: this.translateService.getDefaultLang(),
                                selectedSftpSettingsIds: data.selectedSftpSettingsIds,
                            })}
                        ) {
                            data {
                                id
                            }
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.scheduledReports.updateScheduledReport.data;
                }),
            );
    }

    private createScheduledReport(data: any): Observable<any> {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation {
                    scheduledReports {
                        addScheduledReport(
                            scheduledReport: ${this.deleteEmptyAndPrepareDataForGraphQL({
                                scheduledReportTypeValue: data.scheduledReportType,
                                dayOfMonth: data.dayOfMonth,
                                daysToTriggerValue: data.days ? this.mapDayObjectToBitmask(data.days) : undefined,
                                emailBody: data.emailSettings?.body,
                                emailSubject: data.emailSettings?.subject,
                                runTimeInput: data.time,
                                emailRecipientsInput: data.emails ?? [],
                                scheduledReportFrequencyValue: data.frequency,
                                languageIsoCode: this.translateService.getDefaultLang(),
                                selectedSftpSettingsIds: data.selectedSftpSettingsIds,
                            })}
                        ) {
                            data {
                                id
                            }
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.scheduledReports.addScheduledReport.data;
                }),
            );
    }

    private mapDayObjectToBitmask(dayObject: Days): number | null {
        if (!dayObject) {
            return null;
        }

        let bitmask = 0;

        // Iterate through each day and set the corresponding bit in bitmask
        dayArray.forEach((day: IndividualDay, index: number) => {
            if (dayObject[day]) {
                // This shifts the 1 (0000001) to the left by 6 - index
                // The result is then compared to the bitmask using a bitwise OR
                // ex. 1 (0000001) << 5 = 0100000; 0100000 | 1011111 = 1111111
                bitmask |= 1 << (6 - index);
            }
        });

        return bitmask;
    }

    private mapDayBitmaskToObject(days: number): Days {
        const bitmask = days as unknown as number;
        const daysResult: Days = {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false,
        };

        // Iterate through each day and set the corresponding day in daysResult
        dayArray.forEach((day: IndividualDay, index: number) => {
            // This shifts the 1 (0000001) to the left by 6 - index
            // The result is then compared to the bitmask using a bitwise AND
            // ex. 1 (0000001) << 5 = 0100000; 0100000 & 1011111 = 0000000 (false)
            daysResult[day] = Boolean(bitmask & (1 << (6 - index)));
        });

        return daysResult;
    }

    public deleteScheduledReport(id: string) {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation removeScheduledReports {
                    scheduledReports {
                        removeScheduledReports(
                            scheduledReportIds: ["${id}"]
                        ) {
                            statusCode
                        }
                    }
                }
            `,
                variables: {
                    id: id,
                },
            })
            .pipe(
                map((response: any) => {
                    return response.data.scheduledReports.removeScheduledReports.data;
                }),
            );
    }

    public runScheduledReport(id: string) {
        return this.apollo
            .mutate({
                mutation: gql`
                mutation {
                    scheduledReports {
                        generateReport(scheduledReportId: "${id}") {
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.scheduledReports.generateReport.data;
                }),
            );
    }

    private deleteEmptyAndPrepareDataForGraphQL(data: any) {
        this.deleteEmptyProperties(data);
        return this.stringifyIntoGraphQLQuery(data);
    }

    private stringifyIntoGraphQLQuery(data: any) {
        return JSON.stringify(data).replace(/"([^"]+)":/g, '$1:');
    }

    private deleteEmptyProperties(data: any) {
        Object.keys(data).forEach((key) => {
            if ((data as any)[key] === undefined) {
                delete (data as any)[key];
            }
        });
    }
}
